import * as React from 'react';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { useKundeList } from 'api/kunde';
import classnames from 'classnames';
import { viewStyles } from 'styles/viewStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Lieferantenrechnung, LieferantenrechnungFilter, useLieferantenrechnungList } from 'api/lieferantenrechnung';
import { LieferantenrechnungStatus } from 'formComponents/lookups/LieferantenrechnungStatus';
import { LieferantenrechnungItem } from 'components/Lieferantenrechnung/LieferantenrechnungItem';
import useDebouncedState from 'hooks/useDebouncedState';
import {
    LieferantenrechnungPosition,
    LieferantenrechnungPositionFilter,
    useLieferantenrechnungPositionList,
} from 'api/lieferantenrechnungPosition';
import { DebouncedSearchBox } from 'formComponents/DebouncedSearchBox';
import {
    LieferantenrechnungEdit,
    LieferantenrechnungEditData,
} from 'components/Lieferantenrechnung/LieferantenrechnungEdit';
import { List } from '@fluentui/react/lib/List';
import AutoSizer from 'react-virtualized-auto-sizer';
import moment from 'moment-timezone';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import usePersistentState from 'hooks/usePersistentState';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { useHistory, useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { createRecordUrl, decodeRecordNr } from 'utils/formatter';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';
// eslint-disable-next-line max-len
import { LieferantenrechnungPositionenHeader } from 'components/Lieferantenrechnung/LieferantenrechnungPositionenHeader';

const defaultMaxRows = 250;

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 150px 180px 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall1: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall2: {
        display: 'grid',
        gridTemplateColumns: '150px 150px',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

interface FilterValues {
    maxRows: number;
    kNr?: number;
    nr?: string;
    auNr?: string;
    status: string;
    datumVon?: string;
    datumBis?: string;
    searchText: string;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    kNr: undefined,
    nr: undefined,
    auNr: '',
    status: '__all__',
    datumVon: undefined,
    datumBis: undefined,
    searchText: '',
};

export const LieferantenrechnungView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const [viewMode, setViewMode] = usePersistentState<'short' | 'long'>('long', 'LieferantenrechnungView.viewMode');

    const { urlKundeNr, urlRecordNr } = useParams<RecordViewUrlParams>();
    const kNr = urlKundeNr ? parseInt(urlKundeNr, 10) : undefined;

    const isSmallScreenEmbedded = useMediaQuery({ query: `(max-width: 1800px)` });
    const isSmallScreenNotEmbedded = useMediaQuery({ query: `(max-width: 1450px)` });
    const isSmallScreen = urlKundeNr ? isSmallScreenEmbedded : isSmallScreenNotEmbedded;

    const rechnungEditData = React.useMemo<LieferantenrechnungEditData | null>(() => {
        if (urlRecordNr) {
            return {
                rNr: decodeRecordNr(urlRecordNr),
                kNr,
            };
        }
        return null;
    }, [kNr, urlRecordNr]);

    const kundeList = useKundeList({ nr: kNr || -1 });

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(
        defaultFilterValues,
        1000,
    );

    const filter = React.useMemo<LieferantenrechnungFilter>(() => {
        return {
            kNr,
            status: debouncedFilterValues.status === '__all__' ? undefined : parseInt(debouncedFilterValues.status, 10),
            nrSearch: debouncedFilterValues.nr,
            auNrSearch: debouncedFilterValues.auNr,
            datumVon: debouncedFilterValues.datumVon,
            datumBis: debouncedFilterValues.datumBis,
            searchText: debouncedFilterValues.searchText,
            withLieferant: true,
            maxRows: defaultMaxRows,
        };
    }, [kNr, debouncedFilterValues]);

    const lieferantenrechnungList = useLieferantenrechnungList(filter);

    const positionenFilter = React.useMemo<LieferantenrechnungPositionFilter>(() => {
        if (lieferantenrechnungList.isLoading || lieferantenrechnungList.isLoading || lieferantenrechnungList.isError) {
            return {};
        }

        return {
            rNrList: lieferantenrechnungList.data?.map((lieferantenrechnung) => lieferantenrechnung.nr) || [],
        };
    }, [lieferantenrechnungList]);

    const lieferantenrechnungPositionList = useLieferantenrechnungPositionList(positionenFilter, {
        enabled: false,
    });

    React.useEffect(() => {
        if (positionenFilter.rNrList) {
            lieferantenrechnungPositionList.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionenFilter]);

    const lieferantenrechnungPositionenByANr = React.useMemo<{ [key: string]: LieferantenrechnungPosition[] }>(() => {
        if (
            lieferantenrechnungPositionList.isLoading ||
            lieferantenrechnungPositionList.isLoading ||
            lieferantenrechnungPositionList.isError ||
            (lieferantenrechnungPositionList.data?.length || 0) === 0
        ) {
            return {};
        }

        const newLieferantenrechnungPositionenByANr: { [key: string]: LieferantenrechnungPosition[] } = {};

        lieferantenrechnungPositionList.data?.forEach((rechnungPosition) => {
            if (!newLieferantenrechnungPositionenByANr[rechnungPosition.rNr]) {
                newLieferantenrechnungPositionenByANr[rechnungPosition.rNr] = [];
            }

            newLieferantenrechnungPositionenByANr[rechnungPosition.rNr].push(rechnungPosition);
        });

        return newLieferantenrechnungPositionenByANr;
    }, [lieferantenrechnungPositionList]);

    const onRenderCell = React.useCallback(
        (lieferantenrechnung: Lieferantenrechnung | undefined): JSX.Element => {
            if (!lieferantenrechnung) {
                return <div />;
            }

            return (
                <LieferantenrechnungItem
                    key={lieferantenrechnung.nr}
                    lieferantenrechnung={lieferantenrechnung}
                    withLieferant={!urlKundeNr}
                    viewMode={viewMode}
                    withHeader={false}
                    lieferantenrechnungPositionen={lieferantenrechnungPositionenByANr[lieferantenrechnung.nr] || []}
                    editLieferantenrechnung={() =>
                        history.push(createRecordUrl('lieferantenrechnungen', urlKundeNr, lieferantenrechnung.nr))
                    }
                />
            );
        },
        [lieferantenrechnungPositionenByANr, urlKundeNr, viewMode, history],
    );

    const renderDatumVon = () => (
        <FormDatePicker
            value={filterValues.datumVon}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatumBis = () => (
        <FormDatePicker
            value={filterValues.datumBis}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    if (auth.rolle === 'AD' && kundeList.data?.length === 1) {
        if (kundeList.data[0].betreuer && kundeList.data[0].betreuer !== auth.nr) {
            return (
                <Fabric className={viewStyles.container}>
                    <div className={viewStyles.listContainer}>
                        <div className={viewStyles.noData}>Keine Daten.</div>
                    </div>
                </Fabric>
            );
        }
    }

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric
            className={classnames(viewStyles.container, {
                [viewStyles.twoSearchBarLines]: isSmallScreen,
                [viewStyles.embedded]: !!urlKundeNr,
            })}
        >
            <Helmet>
                <title>
                    {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                    {rechnungEditData?.rNr ? 'Lieferantenrechnung ' + rechnungEditData?.rNr : 'Lieferantenrechnung'}
                </title>
            </Helmet>
            <div className={isSmallScreen ? styles.searchBarSmall1 : styles.searchBar}>
                <DebouncedSearchBox
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            searchText: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Rechnung-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            nr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Auftrag-Nr. (Lief.)"
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            auNr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <LieferantenrechnungStatus
                    value={filterValues.status?.toString()}
                    renderLabel="placeholder"
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            status: newValue,
                        });
                    }}
                    addAll
                />

                {isSmallScreen || renderDatumVon()}
                {isSmallScreen || renderDatumBis()}

                <div />

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Kurzansicht"
                        checked={viewMode === 'short'}
                        onChange={(event, checked) => {
                            setViewMode(checked ? 'short' : 'long');
                        }}
                    />
                </div>

                <PrimaryButton
                    onClick={() => history.push(createRecordUrl('lieferantenrechnungen', urlKundeNr, 'neu'))}
                    text="Neue Lieferantenrechnung"
                />
            </div>

            {isSmallScreen && (
                <div className={styles.searchBarSmall2}>
                    {renderDatumVon()}
                    {renderDatumBis()}
                </div>
            )}

            {lieferantenrechnungList.isLoading || lieferantenrechnungList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <div className={viewStyles.listContainer}>
                    {lieferantenrechnungList?.data && lieferantenrechnungList?.data.length === 0 ? (
                        <div className={viewStyles.noData}>Keine Lieferantenrechnungen.</div>
                    ) : (
                        <>
                            {viewMode === 'short' && (
                                <div className={viewStyles.listContainer}>
                                    <table className={formStyles.stickyPositionenHeaderTable}>
                                        <LieferantenrechnungPositionenHeader />
                                    </table>
                                </div>
                            )}
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div style={{ width, height, overflowX: 'hidden', overflowY: 'scroll' }}>
                                        <List
                                            key={viewMode + (lieferantenrechnungPositionList.isSuccess ? 1 : 0)}
                                            items={lieferantenrechnungList.data || []}
                                            onRenderCell={onRenderCell}
                                        />
                                    </div>
                                )}
                            </AutoSizer>
                        </>
                    )}
                </div>
            )}
            {rechnungEditData !== null && (
                <LieferantenrechnungEdit
                    defaultKNr={rechnungEditData.kNr}
                    rNr={rechnungEditData.rNr}
                    onClose={(refetch) => {
                        history.push(createRecordUrl('lieferantenrechnungen', urlKundeNr));
                        if (refetch) {
                            lieferantenrechnungPositionList.refetch().then(() => {
                                lieferantenrechnungList.refetch().then();
                            });
                        }
                    }}
                    onReopen={(newRNr: string) => {
                        history.push(createRecordUrl('lieferantenrechnungen', urlKundeNr));
                        lieferantenrechnungPositionList.refetch().then(() => {
                            lieferantenrechnungList.refetch().then(() => {
                                history.push(createRecordUrl('lieferantenrechnungen', urlKundeNr, newRNr));
                            });
                        });
                    }}
                />
            )}
        </Fabric>
    );
};
